import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Paperclip, Phone, PhoneMissed, Send, Upload } from "lucide-react";
import { api } from "~api";
import { useAuth } from "~context/auth";
import {
  useMarkAdminPeerChatRead,
  useAdminPeerChatMessages,
  useSendAdminPeerChatMessage,
} from "../hooks/useAdminPeerChat";
import {
  useMarkSellerAdminChatRead,
  useSellerAdminChatMessages,
  useSendSellerAdminChatMessage,
} from "../hooks/useSellerAdminChat";
import { SellerAdminChatAttachment, SellerAdminChatContact, isVoiceCallTimelineItem } from "../types";
import {
  AttachBtn,
  AttachmentFile,
  Bubble,
  BubbleText,
  BubbleTime,
  Composer,
  ComposerInput,
  ComposerMain,
  DropOverlay,
  DropOverlayInner,
  FilePreview,
  FilePreviewItem,
  LoadingText,
  Messages,
  SendBtn,
  VoiceCallEvent,
  VoiceCallTime,
  WindowBody,
} from "./dockStyles";
import {
  formatFileSize,
  formatTime,
  getMessageAttachments,
} from "./utils";

const MAX_FILES = 5;

type Props = {
  contact: SellerAdminChatContact;
  onRead?: () => void;
};

function getApiPathFromUrl(url: string) {
  try {
    const parsed = new URL(url);
    const path = parsed.pathname;
    return path.startsWith("/api") ? path.replace(/^\/api/, "") : path;
  } catch {
    return url;
  }
}

async function downloadAttachmentFile(url: string, filename: string) {
  const path = getApiPathFromUrl(url);
  const response = await api.get(path, { responseType: "blob" });
  const blobUrl = window.URL.createObjectURL(response.data);
  const link = document.createElement("a");
  link.href = blobUrl;
  link.download = filename;
  link.click();
  window.URL.revokeObjectURL(blobUrl);
}

function AttachmentBlock({ attachment }: { attachment: SellerAdminChatAttachment }) {
  if (!attachment.url) {
    return <span className="text-xs text-slate-500">{attachment.filename}</span>;
  }

  return (
    <AttachmentFile
      type="button"
      onClick={() => downloadAttachmentFile(attachment.url!, attachment.filename)}
    >
      <span>
        <strong className="block text-sm truncate">{attachment.filename}</strong>
        <span className="text-xs text-blue-700">Baixar arquivo</span>
        {attachment.size ? (
          <span className="block text-[11px] text-slate-500">
            {formatFileSize(attachment.size)}
          </span>
        ) : null}
      </span>
    </AttachmentFile>
  );
}

export function AdminChatWindow({ contact, onRead }: Props) {
  const { user } = useAuth();
  const isPeerChat = contact.scope === "admin_peer";
  const messagesRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const dragDepthRef = useRef(0);

  const [body, setBody] = useState("");
  const [pendingFiles, setPendingFiles] = useState<File[]>([]);
  const [isDragOver, setIsDragOver] = useState(false);

  const sellerMessagesQuery = useSellerAdminChatMessages(
    isPeerChat ? undefined : contact.id,
  );
  const peerMessagesQuery = useAdminPeerChatMessages(
    isPeerChat ? contact.id : undefined,
  );
  const messagesQuery = isPeerChat ? peerMessagesQuery : sellerMessagesQuery;

  const markSellerRead = useMarkSellerAdminChatRead();
  const markPeerRead = useMarkAdminPeerChatRead();
  const sendSellerMessage = useSendSellerAdminChatMessage();
  const sendPeerMessage = useSendAdminPeerChatMessage();
  const markRead = isPeerChat ? markPeerRead : markSellerRead;
  const sendMessage = isPeerChat ? sendPeerMessage : sendSellerMessage;

  const sortedTimeline = useMemo(
    () => [...(messagesQuery.data ?? [])],
    [messagesQuery.data],
  );

  useEffect(() => {
    markRead.mutate(contact.id, { onSuccess: () => onRead?.() });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contact.id, contact.scope]);

  useEffect(() => {
    const container = messagesRef.current;
    if (!container) return;
    container.scrollTop = container.scrollHeight;
  }, [sortedTimeline.length, sendMessage.isLoading]);

  const mergeFiles = useCallback((incoming: File[]) => {
    setPendingFiles((current) => {
      const merged = [...current];
      incoming.forEach((file) => {
        if (merged.length >= MAX_FILES) return;
        const duplicate = merged.some(
          (existing) =>
            existing.name === file.name &&
            existing.size === file.size &&
            existing.lastModified === file.lastModified,
        );
        if (!duplicate) merged.push(file);
      });
      return merged.slice(0, MAX_FILES);
    });
  }, []);

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!body.trim() && !pendingFiles.length) return;

    if (isPeerChat) {
      await sendPeerMessage.mutateAsync({
        adminId: contact.id,
        body,
        files: pendingFiles,
      });
    } else {
      await sendSellerMessage.mutateAsync({
        sellerId: contact.id,
        body,
        files: pendingFiles,
      });
    }

    setBody("");
    setPendingFiles([]);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const handleDragEnter = (event: React.DragEvent) => {
    event.preventDefault();
    dragDepthRef.current += 1;
    setIsDragOver(true);
  };

  const handleDragLeave = () => {
    dragDepthRef.current = Math.max(0, dragDepthRef.current - 1);
    if (dragDepthRef.current === 0) setIsDragOver(false);
  };

  const handleDrop = (event: React.DragEvent) => {
    event.preventDefault();
    dragDepthRef.current = 0;
    setIsDragOver(false);
    mergeFiles(Array.from(event.dataTransfer.files || []));
  };

  return (
    <WindowBody
      onDragEnter={handleDragEnter}
      onDragOver={(event) => event.preventDefault()}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
    >
      {isDragOver && (
        <DropOverlay>
          <DropOverlayInner>
            <Upload size={28} />
            <strong>Solte os arquivos aqui</strong>
            <span>PDF, XML, Excel, CSV ou imagens</span>
          </DropOverlayInner>
        </DropOverlay>
      )}

      <Messages ref={messagesRef}>
        {messagesQuery.isLoading && (
          <LoadingText>Carregando mensagens...</LoadingText>
        )}
        {sortedTimeline.map((item) => {
          if (isVoiceCallTimelineItem(item)) {
            return (
              <VoiceCallEvent
                key={`call-${item.id}`}
                $answered={item.voice_call.was_answered}
                $outgoing={item.voice_call.is_outgoing}
              >
                {item.voice_call.was_answered ? (
                  <Phone size={14} />
                ) : (
                  <PhoneMissed size={14} />
                )}
                <span>{item.voice_call.label}</span>
                <VoiceCallTime>{formatTime(item.created_at)}</VoiceCallTime>
              </VoiceCallEvent>
            );
          }

          const mine = isPeerChat
            ? item.sender_id === user.id
            : item.sender_type === "admin";

          return (
            <Bubble key={item.id} $mine={mine}>
              {getMessageAttachments(item).map((attachment, index) => (
                <AttachmentBlock
                  key={`${item.id}-${index}`}
                  attachment={attachment}
                />
              ))}
              {item.body ? <BubbleText>{item.body}</BubbleText> : null}
              <BubbleTime>{formatTime(item.created_at)}</BubbleTime>
            </Bubble>
          );
        })}
      </Messages>

      <Composer onSubmit={handleSubmit}>
        <input
          ref={fileInputRef}
          type="file"
          hidden
          multiple
          accept=".pdf,.xml,.xls,.xlsx,.csv,image/jpeg,image/png,image/gif,image/webp"
          onChange={(event) => mergeFiles(Array.from(event.target.files ?? []))}
        />
        <AttachBtn
          type="button"
          aria-label="Anexar arquivo"
          disabled={sendMessage.isLoading}
          onClick={() => fileInputRef.current?.click()}
        >
          <Paperclip size={18} />
        </AttachBtn>
        <ComposerMain>
          {pendingFiles.length > 0 && (
            <FilePreview>
              {pendingFiles.map((file, index) => (
                <FilePreviewItem key={`${file.name}-${file.lastModified}`}>
                  <span className="truncate">{file.name}</span>
                  <button
                    type="button"
                    aria-label="Remover anexo"
                    onClick={() =>
                      setPendingFiles((current) =>
                        current.filter((_, fileIndex) => fileIndex !== index),
                      )
                    }
                  >
                    ×
                  </button>
                </FilePreviewItem>
              ))}
            </FilePreview>
          )}
          <ComposerInput
            rows={1}
            value={body}
            maxLength={5000}
            placeholder="Aa"
            onChange={(event) => setBody(event.target.value)}
          />
        </ComposerMain>
        <SendBtn
          type="submit"
          aria-label="Enviar"
          disabled={sendMessage.isLoading}
        >
          <Send size={16} />
        </SendBtn>
      </Composer>
    </WindowBody>
  );
}
